// Server-rendered SMS challenge page (private platforms, no Stimulus runtime) carries a
// `[data-sms-delivery-status]` element. This polls the Rails delivery-status endpoint and
// reflects the async code delivery in the resend control, mirroring the React
// `useSmsDeliveryStatusPoll` + `useResendCooldown` hooks used on public platforms:
//   - PENDING: keep polling, keep the resend button disabled and showing "sending..."
//   - SENT: stop polling, anchor a live cooldown countdown to the moment it was sent
//   - FAILED: stop polling, surface the delivery-failure message and free the resend button
// The resend itself is a full POST that reloads the page, so polling restarts on its own.

const POLL_INTERVAL_MS = 1000;
const TICK_INTERVAL_MS = 1000;
const SECONDS_PLACEHOLDER = "{{seconds}}";

const PENDING_STATUS = "PENDING";
const SENT_STATUS = "SENT";
const FAILED_STATUS = "FAILED";

// Seconds left before the code may be resent, anchored to the moment it was actually sent
// rather than to render time, so the countdown stays truthful across renders and resends.
const secondsLeftSince = (sentAt, cooldownSeconds) => {
  const elapsedSeconds = (Date.now() - new Date(sentAt).getTime()) / 1000;

  return Math.max(Math.ceil(cooldownSeconds - elapsedSeconds), 0);
};

const startDeliveryStatusPoll = element => {
  const button = element.querySelector("[data-sms-delivery-status-target=button]");
  const label = element.querySelector("[data-sms-delivery-status-target=label]");
  const failedMessage = element.querySelector(
    "[data-sms-delivery-status-target=failedMessage]"
  );

  const {
    smsDeliveryStatusUrlValue: url,
    smsDeliveryStatusCountdownTemplateValue: countdownTemplate,
    smsDeliveryStatusResendLabelValue: resendLabel
  } = element.dataset;

  const cooldownSeconds = parseInt(
    element.dataset.smsDeliveryStatusCooldownSecondsValue,
    10
  );

  let pollInterval;
  let countdownInterval;

  const enableResend = () => {
    if (button) button.disabled = false;
    if (label) label.textContent = resendLabel;
  };

  const renderCountdown = secondsLeft => {
    if (label) {
      label.textContent = countdownTemplate.replace(SECONDS_PLACEHOLDER, secondsLeft);
    }
  };

  const startCountdown = sentAt => {
    if (secondsLeftSince(sentAt, cooldownSeconds) <= 0) {
      enableResend();
      return;
    }

    renderCountdown(secondsLeftSince(sentAt, cooldownSeconds));

    countdownInterval = setInterval(() => {
      const secondsLeft = secondsLeftSince(sentAt, cooldownSeconds);

      if (secondsLeft > 0) {
        renderCountdown(secondsLeft);
        return;
      }

      clearInterval(countdownInterval);
      enableResend();
    }, TICK_INTERVAL_MS);
  };

  const handleDelivery = ({ status, occurred_at: occurredAt }) => {
    if (status === PENDING_STATUS) return;

    clearInterval(pollInterval);

    if (status === SENT_STATUS) {
      startCountdown(occurredAt);
    } else if (status === FAILED_STATUS) {
      if (failedMessage) failedMessage.classList.remove("d-none");
      enableResend();
    }
  };

  const pollDeliveryStatus = async () => {
    try {
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "include"
      });

      if (!response.ok) return;

      handleDelivery(await response.json());
    } catch {
      // A failed poll is not actionable; the next tick will retry.
    }
  };

  pollDeliveryStatus();
  pollInterval = setInterval(pollDeliveryStatus, POLL_INTERVAL_MS);
};

const initDeliveryStatusPolls = () => {
  document
    .querySelectorAll("[data-sms-delivery-status]")
    .forEach(startDeliveryStatusPoll);
};

document.addEventListener("DOMContentLoaded", initDeliveryStatusPolls);
